/**
 * BootScene
 *
 * Drives the PlayServ session to `'active'`, then hydrates the two
 * gameplay-critical entities:
 *   1. `MatchConfig` (singleton) — gameplay tuning consumed by ArenaController.
 *   2. `Player` (per-userId) — created with defaults on first visit, restored
 *      on subsequent visits.
 *
 * Each step has its own banner text so a stuck step is visually obvious.
 *
 * TODO(PSV-1334-followup): subscribe to the Arena group from here too so the
 *   join is acknowledged before ArenaScene paints. Currently the controller
 *   does it inline and the brief subscribe latency is invisible.
 */

import Phaser from 'phaser';

import { getClient } from '../playserv';
import { readGuestIdentity } from '../identity';
import { loadMatchConfig } from '../data/matchConfig';
import { loadOrCreatePlayer, type Player } from '../data/player';

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'Boot' });
  }

  /**
   * PSV-1336 — load ship + background textures up front so the Arena
   * scene can create `Phaser.GameObjects.Sprite` / `Image` instances
   * synchronously. All files live under `public/assets/` and are served
   * at `/assets/...` by Vite's default static handler.
   */
  preload(): void {
    this.load.image('ship_b', 'assets/ship_b.png');
    this.load.image('space_bg', 'assets/space_bg.png');
    // PSV-1337 — three asteroid size tiers. Selected by tier name at
    // spawn time in `AsteroidManager`. Files served from `public/assets/`.
    this.load.image('asteroid_small', 'assets/asteroid_small.png');
    this.load.image('asteroid_medium', 'assets/asteroid_medium.png');
    this.load.image('asteroid_large', 'assets/asteroid_large.png');
  }

  async create(): Promise<void> {
    const client = getClient();
    const banner = this.add.text(20, 20, 'PlayServ — connecting…', {
      fontSize: '18px',
      color: '#eeeeee',
    });

    try {
      await client.ready();
    } catch (err) {
      this.fail(banner, 'Failed to connect', err);
      return;
    }

    banner.setText('Loading match config…').setColor('#eeeeee');
    try {
      await loadMatchConfig(client);
    } catch (err) {
      this.fail(banner, 'Failed to load match config', err);
      return;
    }

    banner.setText('Loading profile…').setColor('#eeeeee');
    const { userId, guestName } = readGuestIdentity();
    let player: Player;
    try {
      player = await loadOrCreatePlayer(client, userId);
    } catch (err) {
      this.fail(banner, 'Failed to load profile', err);
      return;
    }

    banner
      .setText(
        `Welcome, ${guestName}! ` +
          `Ship: ${player.Ship.Class} · K/D: ${player.Stats.Kills}/${player.Stats.Deaths}`,
      )
      .setColor('#4fff7a');

    this.add.text(20, 48, `userId = ${client.session.userId ?? '(none)'}`, {
      fontSize: '13px',
      color: '#888888',
    });

    this.time.delayedCall(800, () => this.scene.start('Arena'));
  }

  private fail(banner: Phaser.GameObjects.Text, prefix: string, err: unknown): void {
    const message = err instanceof Error ? err.message : String(err);
    banner.setText(`${prefix}: ${message}`).setColor('#ff6b6b');
    // eslint-disable-next-line no-console
    console.error(`[boot] ${prefix}:`, err);
  }
}
